import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FormInputController,
  FormTextInput,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  EventKeyMappingsActions,
  useCustomerDispatch,
  useCustomerSelector,
} from '@wajeez-web/stores/customers';
import { enumMapper } from '@wajeez-web/core/utils';
import { Autocomplete } from '@wajeez-web/components/mui';

const eventProviders = enumMapper(Server.EventProvider);

type Input = Server.UpdateEventKeyMappingValueInput;

interface EventKeyMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  id: number;
  onSubmit?: () => void | Promise<void>;
}

export const EventKeyMappingModal = ({
  isOpen,
  onClose,
  id,
  onSubmit,
}: EventKeyMappingModalProps) => {
  const customerDispatch = useCustomerDispatch();

  const editEventKeyMappingStatus = useCustomerSelector(
    ({ eventKeyMappings }) => eventKeyMappings.editEventKeyMapping.status
  );
  const eventKeyMappingById = useCustomerSelector(
    ({ eventKeyMappings }) => eventKeyMappings.eventKeyMappingById
  );

  const { handleSubmit, control, reset } = useForm<Input>();

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    const mappedData: Input = {
      ...data,
      providers: data.providers?.map((p) => Number(p)),
    };

    const res = await customerDispatch(
      EventKeyMappingsActions.editEventKeyMappingAsync(mappedData)
    );

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && (await onSubmit());
    }
  };

  useEffect(() => {
    customerDispatch(EventKeyMappingsActions.getEventKeyMappingByIdAsync(id));
  }, [customerDispatch, id]);

  useEffect(() => {
    if (eventKeyMappingById.data) {
      reset(eventKeyMappingById.data);
    }
  }, [eventKeyMappingById.data]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Event"
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
    >
      <div className="w-full">
        {eventKeyMappingById.status === 'loading' ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="w-full flex flex-col justify-start items-start"
          >
            <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
              <FormTextInput
                name="eventValue"
                control={control}
                fieldType={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: {},
                  label: 'Event Value',
                  placeHolder: 'Event Value',
                }}
                heightClassName="h-[34px]"
                widthClassName="w-full"
              />
              <div className="w-full">
                <FormInputController
                  control={control}
                  name={`providers`}
                  fieldMeta={{
                    fieldType: Server.FieldType.Select,
                    fieldOptions: {},
                  }}
                  renderInput={({ ...props }) => (
                    <div className="h-fit">
                      <Autocomplete
                        id={`providers`}
                        options={eventProviders}
                        label="Providers"
                        heightClassName="h-full"
                        color="var(--theme-primary)"
                        multiple
                        {...props}
                      />
                    </div>
                  )}
                />
              </div>
            </div>
            <div className="mt-6 w-full flex justify-end items-center space-s-2">
              <div className="w-[70px]">
                <ButtonHeadless
                  bgStyle="bg-warmGray-300"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={onClose}
                >
                  <div className="text-warmGray-600 text-[16px] font-medium">
                    <ReturnTranslatedText
                      text="buttons.Cancel"
                      origintext="Cancel"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[70px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Save"
                      origintext="Save"
                    />
                    {editEventKeyMappingStatus === 'loading' && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
